import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import {
  IconAi,
  IconCollapse,
  IconCompanies,
  IconDocuments,
  IconExpand,
  IconFees,
  IconOverview,
  IconPartners,
  IconPayments,
  IconRating,
  IconUsers,
} from './NavIcons';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logout } from '../features/auth/authSlice';

const COLLAPSE_KEY = 'micus_admin_sidebar_collapsed';

type NavItem = { to: string; label: string; end?: boolean; icon: (p: { className?: string }) => ReactNode };

const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: 'Workspace',
    items: [{ to: '/', label: 'Overview', end: true, icon: IconOverview }],
  },
  {
    section: 'People',
    items: [
      { to: '/users', label: 'Users', icon: IconUsers },
      { to: '/companies', label: 'Companies', icon: IconCompanies },
      { to: '/partners', label: 'Partners', icon: IconPartners },
    ],
  },
  {
    section: 'Configuration',
    items: [
      { to: '/fees', label: 'Fees', icon: IconFees },
      { to: '/payments', label: 'Payments', icon: IconPayments },
      { to: '/documents', label: 'Documents', icon: IconDocuments },
      { to: '/ratings', label: 'Ratings', icon: IconRating },
      { to: '/ai-settings', label: 'AI Settings', icon: IconAi },
    ],
  },
];

export default function AdminShell() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user } = useAppSelector((s) => s.auth);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const sidebarInner = (compact: boolean) => (
    <aside
      className={`relative flex h-full flex-col text-white transition-[width] duration-200 ${compact ? 'w-[64px]' : 'w-[224px]'}`}
      style={{ background: 'linear-gradient(170deg, #1a000f 0%, #4d0027 45%, #9e004d 80%, #c4005e 100%)' }}
    >
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-4">
        <Link to="/" className={`flex min-w-0 items-center gap-2 ${compact ? 'w-full justify-center' : ''}`}>
          <BrandLogo size={compact ? 26 : 28} className="shrink-0 brightness-0 invert" />
          {!compact && (
            <div className="min-w-0 leading-tight">
              <p className="truncate text-[15px] font-extrabold tracking-tight">Micus</p>
              <p className="text-[9.5px] font-semibold uppercase tracking-[0.2em] text-white/60">Admin console</p>
            </div>
          )}
        </Link>
      </div>

      <nav className="flex flex-1 flex-col gap-3 overflow-y-auto px-2 py-3">
        {NAV.map((group) => (
          <div key={group.section}>
            {!compact && <p className="mb-1 px-2.5 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-white/40">{group.section}</p>}
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    title={item.label}
                    className={({ isActive }) =>
                      `flex items-center rounded-lg text-[13px] font-medium transition-colors ${compact ? 'justify-center px-2 py-2' : 'gap-2.5 px-2.5 py-2'} ${
                        isActive ? 'bg-white text-[#b8005e] shadow-[0_6px_18px_rgba(0,0,0,0.18)]' : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    {!compact && <span className="truncate">{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 px-2 py-3">
        {!compact && (
          <div className="mb-2 flex items-center gap-2 px-1.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-xs font-bold">
              {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[12.5px] font-semibold">{user?.name || 'Admin'}</p>
              <p className="truncate text-[11px] text-white/60">{user?.email}</p>
            </div>
          </div>
        )}
        <div className={`flex ${compact ? 'flex-col' : ''} gap-1`}>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="hidden items-center justify-center rounded-lg px-2 py-1.5 text-white/80 hover:bg-white/10 hover:text-white lg:flex"
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {collapsed ? <IconExpand className="h-4 w-4" /> : <IconCollapse className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => dispatch(logout())}
            className="flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10"
            style={{ border: '1px solid rgba(255,255,255,0.18)' }}
            title="Sign out"
          >
            {compact ? 'Out' : 'Sign out'}
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-[#fbf7f9] text-[#3b0a22]">
      <div className="sticky top-0 hidden h-screen shrink-0 lg:block">{sidebarInner(collapsed)}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" aria-label="Close menu" className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 shadow-2xl">{sidebarInner(false)}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-[#f6dbe7] bg-white/90 px-4 py-2.5 backdrop-blur-md lg:hidden">
          <button type="button" onClick={() => setMobileOpen(true)} className="mz-btn-ghost !px-3 !py-1.5 text-xs">
            Menu
          </button>
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size={24} />
            <span className="text-sm font-bold text-[#6b0030]">Micus Admin</span>
          </Link>
          <button type="button" onClick={() => dispatch(logout())} className="mz-btn-ghost !px-3 !py-1.5 text-xs">
            Out
          </button>
        </header>

        <main className="min-w-0 flex-1">
          <div className="w-full max-w-none px-3 py-5 sm:px-4 lg:px-5 xl:px-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
